import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { auth, db } from '../firebase';
import { doc, setDoc, getDoc } from 'firebase/firestore';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [userDataReady, setUserDataReady] = useState(false);
  const userDataCacheRef = useRef({ uid: null, inflight: null });

  // Sign up function
  async function signup(email, password, displayName) {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    
    // Update profile with display name
    await updateProfile(result.user, { displayName });
    
    // Create user document in Firestore
    await setDoc(doc(db, 'users', result.user.uid), {
      email: result.user.email,
      displayName: displayName,
      createdAt: new Date(),
      likedProducts: [],
      customLists: [],
      savedSearches: []
    });
    
    return result;
  }

  // Sign in function
  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  // Sign out function
  function logout() {
    return signOut(auth);
  }

  // Reset password function
  function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }

  const fetchUserDataForUid = useCallback(async (uid) => {
    if (!uid) return null;

    if (userDataCacheRef.current.uid === uid && userDataCacheRef.current.inflight) {
      return userDataCacheRef.current.inflight;
    }

    const promise = getDoc(doc(db, 'users', uid)).then((userDoc) => (
      userDoc.exists() ? userDoc.data() : null
    ));

    userDataCacheRef.current = { uid, inflight: promise };

    try {
      const data = await promise;
      setUserData(data);
      setUserDataReady(true);
      userDataCacheRef.current = { uid, inflight: null };
      return data;
    } catch (error) {
      userDataCacheRef.current = { uid: null, inflight: null };
      setUserDataReady(false);
      throw error;
    }
  }, []);

  const invalidateUserDataCache = useCallback(() => {
    userDataCacheRef.current = { uid: null, inflight: null };
    setUserData(null);
    setUserDataReady(false);
  }, []);

  const patchUserData = useCallback((updates) => {
    setUserData((prev) => (prev ? { ...prev, ...updates } : updates));
  }, []);

  // Update user profile
  async function updateUserProfile(updates) {
    if (currentUser) {
      await updateProfile(currentUser, updates);
      await setDoc(doc(db, 'users', currentUser.uid), updates, { merge: true });
      patchUserData(updates);
    }
  }

  // Get user data from Firestore (deduped — one read per login session)
  async function getUserData() {
    if (!currentUser) return null;
    if (userDataCacheRef.current.uid === currentUser.uid && !userDataCacheRef.current.inflight) {
      return userData;
    }
    return fetchUserDataForUid(currentUser.uid);
  }

  // Google sign-in function
  async function signInWithGoogle() {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    // If new user, create Firestore user doc
    if (result.user && result.user.metadata.creationTime === result.user.metadata.lastSignInTime) {
      await setDoc(doc(db, 'users', result.user.uid), {
        email: result.user.email,
        displayName: result.user.displayName,
        createdAt: new Date(),
        likedProducts: [],
        customLists: [],
        savedSearches: []
      });
    }
    return result;
  }

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Single Firestore read when user signs in (shared by header, browse, profile)
  useEffect(() => {
    if (!currentUser) {
      invalidateUserDataCache();
      return;
    }
    fetchUserDataForUid(currentUser.uid).catch((error) => {
      console.error('Error loading user data:', error);
    });
  }, [currentUser, fetchUserDataForUid, invalidateUserDataCache]);

  const value = {
    currentUser,
    authLoading: loading,
    userData,
    userDataReady,
    patchUserData,
    invalidateUserDataCache,
    signup,
    login,
    logout,
    resetPassword,
    updateUserProfile,
    getUserData,
    signInWithGoogle
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
} 